import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

// Module-level state so all interceptor calls share the same refresh cycle
let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<string | null>(null);

const addBearer = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
    req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

const isRefreshOrLogout = (url: string): boolean =>
    url.includes('auth/refresh') || url.includes('auth/logout');

export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const auth = inject(AuthService);
    const token = auth.getToken();

    const authReq = token ? addBearer(req, token) : req;

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Only handle 401s; skip refresh/logout endpoints to avoid infinite loops
            if (error.status !== 401 || isRefreshOrLogout(req.url)) {
                return throwError(() => error);
            }

            if (isRefreshing) {
                // Another request is already refreshing — queue this one
                // and retry it once the new token arrives
                return refreshedToken$.pipe(
                    filter(t => t !== null),
                    take(1),
                    switchMap(newToken => next(addBearer(req, newToken!)))
                );
            }

            isRefreshing = true;
            refreshedToken$.next(null);

            return from(auth.refreshAccessToken()).pipe(
                switchMap(success => {
                    isRefreshing = false;
                    if (success) {
                        const newToken = auth.getToken()!;
                        refreshedToken$.next(newToken);
                        return next(addBearer(req, newToken));
                    }
                    // Refresh token invalid/expired — force logout
                    auth.logout();
                    return throwError(() => error);
                }),
                catchError(err => {
                    isRefreshing = false;
                    auth.logout();
                    return throwError(() => err);
                })
            );
        })
    );
};
